// RecuperarContrasenaSection.js — Sección `recuperar_contrasena`: el formulario de
// «¿Has olvidado tu contraseña?» dentro de una página del proyecto, con su diseño
// (la página «Restablecer» de LAAC Academy, docs/Lac/olvido.png).
//
// Sin token pide el correo y manda el enlace; con ?token=… (el enlace del correo)
// pide la contraseña nueva. La lógica es la misma de /recuperar-contrasena
// (hooks/useRecuperarContrasena); aquí solo cambian los textos y el diseño.
// Para que el enlace del correo abra esta página: ficha → Login y Registro →
// «¿Has olvidado tu contraseña?» → esta página (auth_config.recuperar_pagina).
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import useRecuperarContrasena from '../../../hooks/useRecuperarContrasena';
import { crearActivador } from './accionesBoton';
import VerContrasena from '../../VerContrasena';

const bajar = keyframes`from { opacity: 0; transform: translate3d(0, -30px, 0); } to { opacity: 1; transform: none; }`;

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  font-family: Arial, Helvetica, sans-serif;
  @media (max-width: 980px) { padding: ${p => p.$paddingMovil}; }
`;

const Tarjeta = styled.div`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  box-sizing: border-box;
  background: ${p => p.$fondo};
  border-radius: ${p => p.$radio};
  padding: ${p => p.$padding};
  ${p => p.$sombra && `box-shadow: ${p.$sombra};`}
  ${p => p.$animar && css`
    animation: ${bajar} 700ms ease both;
    @media (prefers-reduced-motion: reduce) { animation: none; }
  `}
  @media (max-width: 767px) { padding: 26px 20px 30px; }
`;

const Titulo = styled.h2`
  margin: 0 0 8px;
  padding: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.$color};
`;

const Texto = styled.p`
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.5;
  color: ${p => p.$color};
`;

const Etiqueta = styled.label`
  display: block;
  margin: 12px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$color};
`;

const Campo = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid ${p => p.$borde};
  border-radius: ${p => p.$radio};
  font-size: 14px;
  font-family: inherit;
  outline: none;
  &:focus { border-color: ${p => p.$foco}; box-shadow: 0 0 0 1px ${p => p.$foco}66; }
`;

const Boton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 14px;
  padding: 14px 20px;
  border: none;
  border-radius: ${p => p.$radio};
  background: ${p => p.$fondo};
  color: ${p => p.$color};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition: box-shadow .2s ease, transform .1s ease;
  &:hover { box-shadow: 0 8px 25px rgba(49, 151, 149, 0.4); transform: translateY(-1px); }
  &:active { transform: none; box-shadow: none; }
  &:disabled { opacity: .6; cursor: default; transform: none; box-shadow: none; }
  ${p => p.$secundario && `
    background: #ffffff;
    color: ${p.$colorSecundario};
    border: 1px solid ${p.$colorSecundario};
  `}
  @media (max-width: 767px) { width: 100%; }
`;

const Acciones = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
  ${Boton} { margin-top: 0; }
`;

const Aviso = styled.div`
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
  background: ${p => (p.$error ? '#fef2f2' : '#ecfdf5')};
  color: ${p => (p.$error ? '#b91c1c' : '#166534')};
`;

const Volver = styled(Link)`
  display: inline-block;
  margin-top: 16px;
  font-size: 13px;
  color: #6b7280;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const RecuperarContrasenaSection = ({ config = {}, openLogin, openRegister, tl = (v, d = '') => v || d }) => {
  const c = config;
  const r = useRecuperarContrasena();
  const navigate = useNavigate();
  const activar = crearActivador({ navigate, openLogin, openRegister });
  const colores = {
    titulo: c.color_titulo || '#0f2a42',
    texto: c.color_texto || '#4b5563',
    etiqueta: c.color_etiqueta || '#374151',
  };
  const boton = { $fondo: c.color_boton || 'linear-gradient(135deg, #0f2a42, #1a3a5a)', $color: c.color_texto_boton || '#ffffff', $radio: c.radio_boton || '12px' };
  const colorSecundario = c.color_boton_secundario || '#1a3a5a';
  const campo = { $borde: c.color_borde_campo || '#d1d5db', $foco: c.color_foco_campo || '#1a3a5a', $radio: c.radio_campo || '10px' };
  const destinoAcceso = c.url_acceso || '/login';

  let contenido;
  if (r.token) {
    contenido = (
      <>
        {tl(c.titulo_nueva, 'Crea tu contraseña nueva') && <Titulo $color={colores.titulo}>{tl(c.titulo_nueva, 'Crea tu contraseña nueva')}</Titulo>}
        {r.tokenOk === null && <Texto $color={colores.texto}>Comprobando el enlace…</Texto>}
        {r.aviso && <Aviso role={r.aviso.error ? 'alert' : 'status'} $error={r.aviso.error}>{r.aviso.texto}</Aviso>}
        {r.tokenOk && !r.listo && (
          <form onSubmit={r.cambiar}>
            <Texto $color={colores.texto}>
              {r.nombre ? `Hola ${r.nombre}, escribe` : 'Escribe'} tu contraseña nueva. Mínimo {r.minimo} caracteres.
            </Texto>
            <Etiqueta htmlFor="rc_password" $color={colores.etiqueta}>Contraseña nueva</Etiqueta>
            <VerContrasena flotante color={colores.etiqueta}>
              {tipo => (
                <Campo id="rc_password" type={tipo} autoComplete="new-password" {...campo}
                  value={r.password} onChange={e => r.setPassword(e.target.value)} autoFocus />
              )}
            </VerContrasena>
            <Etiqueta htmlFor="rc_repetir" $color={colores.etiqueta}>Repite la contraseña</Etiqueta>
            <VerContrasena flotante color={colores.etiqueta}>
              {tipo => (
                <Campo id="rc_repetir" type={tipo} autoComplete="new-password" {...campo}
                  value={r.repetir} onChange={e => r.setRepetir(e.target.value)} />
              )}
            </VerContrasena>
            <Boton type="submit" disabled={r.enviando} {...boton}>
              {r.enviando ? 'Guardando…' : tl(c.texto_boton_nueva, 'Guardar contraseña')}
            </Boton>
          </form>
        )}
        {r.listo && <Boton as={Link} to={destinoAcceso} {...boton}>{tl(c.texto_ir_acceso, 'Iniciar sesión')}</Boton>}
        {r.tokenOk === false && <div><Volver to={window.location.pathname}>{tl(c.texto_pedir_otro, 'Pedir un enlace nuevo')}</Volver></div>}
      </>
    );
  } else {
    // Aviso: el texto propio de la sección si lo tiene; si no, el del servidor.
    const textoAviso = r.aviso && (r.aviso.error
      ? (r.noRegistrado ? tl(c.texto_no_registrado, r.aviso.texto) : r.aviso.texto)
      : tl(c.texto_enviado, r.aviso.texto));
    contenido = (
      <>
        {tl(c.titulo, '') && <Titulo $color={colores.titulo}>{tl(c.titulo, '')}</Titulo>}
        {r.aviso && <Aviso role={r.aviso.error ? 'alert' : 'status'} $error={r.aviso.error}>{textoAviso}</Aviso>}

        {r.noRegistrado && (
          <Acciones>
            <Boton type="button" {...boton} $secundario $colorSecundario={colorSecundario} onClick={r.reintentar}>
              {tl(c.texto_reintentar, 'Intentar de nuevo')}
            </Boton>
            {tl(c.texto_crear_cuenta, 'Crear cuenta') && c.accion_crear_cuenta !== 'ninguna' && (
              <Boton type="button" {...boton}
                onClick={() => activar({ accion: c.accion_crear_cuenta || 'registro', url: c.url_crear_cuenta })}>
                {tl(c.texto_crear_cuenta, 'Crear cuenta')}
              </Boton>
            )}
          </Acciones>
        )}

        {r.listo && (
          <>
            {tl(c.texto_spam, '¿No recibiste el correo? Verifica tu carpeta de spam o promociones.') && (
              <Texto $color={colores.texto}>{tl(c.texto_spam, '¿No recibiste el correo? Verifica tu carpeta de spam o promociones.')}</Texto>
            )}
            <Boton type="button" disabled={r.enviando} {...boton} onClick={() => r.solicitar()}>
              {r.enviando ? 'Enviando…' : tl(c.texto_reenviar, 'Reenviar enlace')}
            </Boton>
          </>
        )}

        {!r.listo && !r.noRegistrado && (
          <form onSubmit={r.solicitar}>
            <Texto $color={colores.texto}>{tl(c.texto, 'Ingresa el correo electrónico o usuario con el que te registraste.')}</Texto>
            <Etiqueta htmlFor="rc_identificador" $color={colores.etiqueta}>{tl(c.etiqueta, 'Correo electrónico o usuario')}</Etiqueta>
            <Campo id="rc_identificador" type="text" autoComplete="username" required {...campo}
              placeholder={tl(c.placeholder, '')}
              value={r.identificador} onChange={e => r.setIdentificador(e.target.value)} />
            <Boton type="submit" disabled={r.enviando} {...boton}>
              {r.enviando ? 'Enviando…' : tl(c.texto_boton, 'Restablecer contraseña')}
            </Boton>
          </form>
        )}
      </>
    );
  }

  return (
    <Seccion
      $fondo={c.fondo || '#f8f5ff'}
      $padding={c.padding || '60px 0 70px'}
      $paddingMovil={c.padding_movil || '40px 0 50px'}
    >
      <Tarjeta
        $ancho={c.ancho_maximo || '700px'}
        $fondo={c.color_tarjeta || '#ffffff'}
        $radio={c.radio_tarjeta || '23px'}
        $padding={c.padding_tarjeta || '40px 40px 70px'}
        $sombra={c.sombra_tarjeta}
        $animar={c.animar !== false}
      >
        {contenido}
      </Tarjeta>
    </Seccion>
  );
};

export default RecuperarContrasenaSection;
